/**
 * Wire format. Every message starts with a 1-byte kind.
 *
 *  rel   0x01 CTRL     [kind][u16 sigLen][sig][utf8 JSON]   signed control message
 *  rel   0x02 BLOB     [kind][u32 blobId][u16 idx][u16 n][bytes]  part of a large payload
 *                      (snapshot); integrity via the sha256 in the signed meta message
 *  unrel 0x10 PSTATE   client -> host: own player state
 *  unrel 0x11 PLAYERS  host -> all: every player's state (slot-indexed)
 *  unrel 0x12 ENTS     host -> all: mob state (delta or keyframe)
 *  unrel 0x13 HBEAT    host -> all: term, lastSeq, tick, sentAt, world time
 *  unrel 0x14 PONG     client -> host: echoed sentAt + lastSeq
 *  unrel 0x15 DROPS    host -> all: dropped items + XP orbs (delta or keyframe)
 *  unrel 0x16 ARROWS   host -> all: arrows (delta or keyframe)
 *  unrel 0x17 BE       host -> all: chest / furnace block-entity contents
 *  rel   0x18 TAKE     client -> host: a drop left the world (picked up, burnt, merged)
 *  rel   0x19 DROP     client -> host: "I dropped this stack here", host spawns it
 *  rel   0x1a BEREQ    client -> host: block-entity contents the client changed
 *  rel   0x1b TNT      client -> host: "I lit this TNT", host primes the charge
 *  unrel 0x1c DMG      host -> one client: "you took this much damage from that"
 *
 * Positions are float32 (sub-mm precision near spawn, ~2 mm at 30k blocks);
 * angles are quantized to u16/i16.
 */

import { deserializeExtras, serializeExtras, type Stack } from '../game/types';

export const K_CTRL = 0x01;
export const K_BLOB = 0x02;
export const K_PSTATE = 0x10;
export const K_PLAYERS = 0x11;
export const K_ENTS = 0x12;
export const K_HBEAT = 0x13;
export const K_PONG = 0x14;
/** Gameplay objects (net/objects.ts): the host owns them, clients mirror them. */
export const K_DROPS = 0x15;
export const K_ARROWS = 0x16;
export const K_BE = 0x17;
export const K_TAKE = 0x18;
export const K_DROP = 0x19;
export const K_BEREQ = 0x1a;
export const K_TNT = 0x1b;
export const K_DMG = 0x1c;

export const MAX_MSG = 256 * 1024;
export const BLOB_PART = 16 * 1024;

const TAU = Math.PI * 2;

export class Writer {
  private buf: Uint8Array;
  private dv: DataView;
  pos = 0;
  constructor(cap = 256) {
    this.buf = new Uint8Array(cap);
    this.dv = new DataView(this.buf.buffer);
  }
  private need(n: number): void {
    if (this.pos + n <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < this.pos + n) cap *= 2;
    const b = new Uint8Array(cap);
    b.set(this.buf);
    this.buf = b;
    this.dv = new DataView(b.buffer);
  }
  u8(v: number): this { this.need(1); this.dv.setUint8(this.pos, v); this.pos += 1; return this; }
  u16(v: number): this { this.need(2); this.dv.setUint16(this.pos, v); this.pos += 2; return this; }
  i16(v: number): this { this.need(2); this.dv.setInt16(this.pos, v); this.pos += 2; return this; }
  u32(v: number): this { this.need(4); this.dv.setUint32(this.pos, v >>> 0); this.pos += 4; return this; }
  i32(v: number): this { this.need(4); this.dv.setInt32(this.pos, v | 0); this.pos += 4; return this; }
  f32(v: number): this { this.need(4); this.dv.setFloat32(this.pos, v); this.pos += 4; return this; }
  f64(v: number): this { this.need(8); this.dv.setFloat64(this.pos, v); this.pos += 8; return this; }
  bytes(b: Uint8Array): this { this.need(b.length); this.buf.set(b, this.pos); this.pos += b.length; return this; }
  /** Angle in radians -> u16 (wraps). */
  angle(a: number): this { return this.u16(Math.round((((a % TAU) + TAU) % TAU) / TAU * 65536) & 0xffff); }
  done(): Uint8Array { return this.buf.slice(0, this.pos); }
}

export class Reader {
  private readonly dv: DataView;
  pos = 0;
  constructor(readonly buf: Uint8Array) {
    this.dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  get left(): number { return this.buf.length - this.pos; }
  private need(n: number): void { if (this.pos + n > this.buf.length) throw new RangeError('short message'); }
  u8(): number { this.need(1); const v = this.dv.getUint8(this.pos); this.pos += 1; return v; }
  u16(): number { this.need(2); const v = this.dv.getUint16(this.pos); this.pos += 2; return v; }
  i16(): number { this.need(2); const v = this.dv.getInt16(this.pos); this.pos += 2; return v; }
  u32(): number { this.need(4); const v = this.dv.getUint32(this.pos); this.pos += 4; return v; }
  i32(): number { this.need(4); const v = this.dv.getInt32(this.pos); this.pos += 4; return v; }
  f32(): number { this.need(4); const v = this.dv.getFloat32(this.pos); this.pos += 4; return v; }
  f64(): number { this.need(8); const v = this.dv.getFloat64(this.pos); this.pos += 8; return v; }
  bytes(n: number): Uint8Array { this.need(n); const b = this.buf.subarray(this.pos, this.pos + n); this.pos += n; return b; }
  angle(): number { const a = this.u16() / 65536 * TAU; return a > Math.PI ? a - TAU : a; }
}

const enc = new TextEncoder();
const dec = new TextDecoder();
export const utf8 = (s: string): Uint8Array => enc.encode(s);
export const fromUtf8 = (b: Uint8Array): string => dec.decode(b);

// ------------------------------------------------------------------ control

export interface Ctrl {
  /** Message type, e.g. 'hello', 'edits', 'chat'. */
  t: string;
  /** Sender identity id. */
  from: string;
  /** Per-sender monotonic counter (replay protection). */
  n: number;
  /** Sender's view of the current term. */
  term: number;
  [k: string]: unknown;
}

export function encodeCtrl(body: Uint8Array, sig: Uint8Array): Uint8Array {
  return new Writer(body.length + sig.length + 4).u8(K_CTRL).u16(sig.length).bytes(sig).bytes(body).done();
}

export function decodeCtrl(data: Uint8Array): { sig: Uint8Array; body: Uint8Array; msg: Ctrl } {
  const r = new Reader(data);
  if (r.u8() !== K_CTRL) throw new Error('not ctrl');
  const sl = r.u16();
  if (sl > 256) throw new Error('bad sig length');
  const sig = r.bytes(sl);
  const body = r.bytes(r.left);
  const msg = JSON.parse(fromUtf8(body)) as Ctrl;
  if (!msg || typeof msg.t !== 'string' || typeof msg.from !== 'string' || typeof msg.n !== 'number' || typeof msg.term !== 'number') {
    throw new Error('bad ctrl schema');
  }
  return { sig, body, msg };
}

// --------------------------------------------------------------------- blob

export function encodeBlobParts(blobId: number, data: Uint8Array): Uint8Array[] {
  const n = Math.max(1, Math.ceil(data.length / BLOB_PART));
  const out: Uint8Array[] = [];
  for (let i = 0; i < n; i++) {
    const part = data.subarray(i * BLOB_PART, Math.min(data.length, (i + 1) * BLOB_PART));
    out.push(new Writer(part.length + 9).u8(K_BLOB).u32(blobId).u16(i).u16(n).bytes(part).done());
  }
  return out;
}

export function decodeBlobPart(data: Uint8Array): { blobId: number; idx: number; n: number; part: Uint8Array } {
  const r = new Reader(data);
  if (r.u8() !== K_BLOB) throw new Error('not blob');
  const blobId = r.u32(), idx = r.u16(), n = r.u16();
  if (idx >= n) throw new Error('bad blob index');
  return { blobId, idx, n, part: r.bytes(r.left).slice() };
}

// ------------------------------------------------------------ player state

export const PF_SNEAK = 1, PF_SPRINT = 2, PF_SWIM = 4, PF_FLY = 8, PF_BOW = 16, PF_FIRE = 32, PF_DEAD = 64;

export interface PlayerState {
  tick: number;
  x: number; y: number; z: number;
  yaw: number; headYaw: number; pitch: number;
  limbSwing: number; limbSwingAmount: number;
  swing: number;
  flags: number;
  hurtTime: number;
  heldItem: number;
  armor: [number, number, number, number];
  health: number;
}

export function emptyPlayerState(): PlayerState {
  return {
    tick: 0, x: 0, y: 0, z: 0, yaw: 0, headYaw: 0, pitch: 0, limbSwing: 0, limbSwingAmount: 0,
    swing: 0, flags: 0, hurtTime: 0, heldItem: 0, armor: [0, 0, 0, 0], health: 20,
  };
}

function writePlayer(w: Writer, p: PlayerState): void {
  w.u32(p.tick).f32(p.x).f32(p.y).f32(p.z).angle(p.yaw).angle(p.headYaw)
    .i16(Math.round(Math.max(-1.6, Math.min(1.6, p.pitch)) * 10000))
    .f32(p.limbSwing).u8(Math.round(Math.max(0, Math.min(1, p.limbSwingAmount)) * 255))
    .u8(Math.round(Math.max(0, Math.min(1, p.swing)) * 255)).u8(p.flags & 0xff).u8(Math.max(0, Math.min(255, p.hurtTime | 0)))
    .u16(p.heldItem & 0xffff);
  for (let i = 0; i < 4; i++) w.u16(p.armor[i] & 0xffff);
  w.u8(Math.max(0, Math.min(255, Math.round(p.health))));
}

function readPlayer(r: Reader): PlayerState {
  const tick = r.u32(), x = r.f32(), y = r.f32(), z = r.f32();
  const yaw = r.angle(), headYaw = r.angle(), pitch = r.i16() / 10000;
  const limbSwing = r.f32(), limbSwingAmount = r.u8() / 255, swing = r.u8() / 255;
  const flags = r.u8(), hurtTime = r.u8(), heldItem = r.u16();
  const armor: [number, number, number, number] = [r.u16(), r.u16(), r.u16(), r.u16()];
  const health = r.u8();
  if (![x, y, z, limbSwing].every(Number.isFinite)) throw new Error('non-finite player state');
  return { tick, x, y, z, yaw, headYaw, pitch, limbSwing, limbSwingAmount, swing, flags, hurtTime, heldItem, armor, health };
}

export function encodePState(p: PlayerState): Uint8Array {
  const w = new Writer(48).u8(K_PSTATE);
  writePlayer(w, p);
  return w.done();
}

export function decodePState(data: Uint8Array): PlayerState {
  const r = new Reader(data);
  if (r.u8() !== K_PSTATE) throw new Error('not pstate');
  return readPlayer(r);
}

export function encodePlayers(list: Array<{ slot: number; s: PlayerState }>): Uint8Array {
  const w = new Writer(8 + list.length * 48).u8(K_PLAYERS).u8(list.length);
  for (const { slot, s } of list) { w.u16(slot); writePlayer(w, s); }
  return w.done();
}

export function decodePlayers(data: Uint8Array): Array<{ slot: number; s: PlayerState }> {
  const r = new Reader(data);
  if (r.u8() !== K_PLAYERS) throw new Error('not players');
  const n = r.u8();
  const out: Array<{ slot: number; s: PlayerState }> = [];
  for (let i = 0; i < n; i++) out.push({ slot: r.u16(), s: readPlayer(r) });
  return out;
}

// ------------------------------------------------------------ entity state

export const MOB_TYPES = ['zombie', 'skeleton', 'creeper', 'spider', 'pig', 'cow', 'sheep', 'chicken', 'wither', 'warden'] as const;
/**
 * Enchantments / anvil name / firework data of ONE stack, as the tail
 * game/types.ts writes after a flat stack list (empty = a plain stack). It
 * rides behind the fixed fields of a message, so a reader that stops early
 * still sees a valid plain stack.
 */
export function stackExtras(s: Stack): number[] {
  const out: number[] = [];
  serializeExtras([s], out);
  return out.length <= EX_MAX ? out : [];
}
/** A stack rebuilt from its wire fields plus an extras tail. */
export function stackWith(id: number, count: number, dmg: number, ex?: readonly number[]): Stack {
  const s: Stack = { id, count, dmg };
  if (ex && ex.length) deserializeExtras([s], ex, 0);
  return s;
}
const EX_MAX = 255;
function writeEx(w: Writer, ex: readonly number[] | undefined): void {
  const n = ex && ex.length <= EX_MAX ? ex.length : 0;
  w.u8(n);
  for (let i = 0; i < n; i++) w.i32(ex![i]);
}
function readEx(r: Reader): number[] | undefined {
  if (r.left < 1) return undefined;
  const n = r.u8();
  if (n * 4 > r.left) throw new Error('bad extras');
  const ex: number[] = [];
  for (let i = 0; i < n; i++) ex.push(r.i32());
  return ex.length ? ex : undefined;
}
const NAME_MAX = 50;

export const EF_FIRE = 1, EF_AGGRO = 2, EF_SHEARED = 4, EF_DYING = 8;

export interface EntityState {
  id: number;
  type: number; // index in MOB_TYPES
  flags: number;
  x: number; y: number; z: number;
  yaw: number; headYaw: number; pitch: number;
  limbSwing: number; limbSwingAmount: number;
  hurtTime: number; deathTime: number;
  swell: number; attackAnim: number;
  color: number; heldItem: number;
  /** Name-tag name ('' / absent = unnamed). */
  name?: string;
}

export interface EntitiesMsg {
  tick: number;
  /** true: the list is complete, anything not in it is gone. */
  keyframe: boolean;
  ents: EntityState[];
  removed: number[];
}

export function encodeEntities(m: EntitiesMsg): Uint8Array {
  const w = new Writer(16 + m.ents.length * 40 + m.removed.length * 4)
    .u8(K_ENTS).u32(m.tick).u8(m.keyframe ? 1 : 0).u16(m.ents.length);
  for (const e of m.ents) {
    w.u32(e.id).u8(e.type).u8(e.flags).f32(e.x).f32(e.y).f32(e.z).angle(e.yaw).angle(e.headYaw)
      .i16(Math.round(Math.max(-3.2, Math.min(3.2, e.pitch)) * 10000)).f32(e.limbSwing)
      .u8(Math.round(Math.max(0, Math.min(1, e.limbSwingAmount)) * 255))
      .u8(Math.max(0, Math.min(255, e.hurtTime | 0))).u8(Math.max(0, Math.min(255, e.deathTime | 0)))
      .u8(Math.round(Math.max(0, Math.min(1, e.swell)) * 255)).u8(Math.round(Math.max(0, Math.min(1, e.attackAnim)) * 255))
      .u8(e.color & 0xff).u16(e.heldItem & 0xffff);
  }
  w.u16(m.removed.length);
  for (const id of m.removed) w.u32(id);
  // named mobs trail the message: [id, length, utf8]; an ent without an entry is unnamed
  const named = m.ents.filter((e) => e.name);
  w.u16(named.length);
  for (const e of named) {
    const b = utf8(e.name!.slice(0, NAME_MAX));
    w.u32(e.id).u8(b.length).bytes(b);
  }
  return w.done();
}

export function decodeEntities(data: Uint8Array): EntitiesMsg {
  const r = new Reader(data);
  if (r.u8() !== K_ENTS) throw new Error('not ents');
  const tick = r.u32(), keyframe = r.u8() === 1, n = r.u16();
  const ents: EntityState[] = [];
  for (let i = 0; i < n; i++) {
    const id = r.u32(), type = r.u8(), flags = r.u8(), x = r.f32(), y = r.f32(), z = r.f32();
    const yaw = r.angle(), headYaw = r.angle(), pitch = r.i16() / 10000, limbSwing = r.f32();
    const limbSwingAmount = r.u8() / 255, hurtTime = r.u8(), deathTime = r.u8();
    const swell = r.u8() / 255, attackAnim = r.u8() / 255, color = r.u8(), heldItem = r.u16();
    if (type >= MOB_TYPES.length || ![x, y, z].every(Number.isFinite)) throw new Error('bad entity');
    ents.push({ id, type, flags, x, y, z, yaw, headYaw, pitch, limbSwing, limbSwingAmount, hurtTime, deathTime, swell, attackAnim, color, heldItem });
  }
  const rn = r.u16();
  const removed: number[] = [];
  for (let i = 0; i < rn; i++) removed.push(r.u32());
  if (r.left >= 2) {
    const byId = new Map(ents.map((e) => [e.id, e]));
    for (let i = r.u16(); i > 0; i--) {
      const id = r.u32(), len = r.u8();
      const e = byId.get(id);
      const name = fromUtf8(r.bytes(len)).slice(0, NAME_MAX);
      if (e && name) e.name = name;
    }
  }
  return { tick, keyframe, ents, removed };
}

// --------------------------------------------------------------- heartbeat

export interface Heartbeat { term: number; lastSeq: number; tick: number; sentAt: number; time: number }

export function encodeHeartbeat(h: Heartbeat): Uint8Array {
  return new Writer(32).u8(K_HBEAT).u32(h.term).u32(h.lastSeq).u32(h.tick).f64(h.sentAt).f32(h.time).done();
}
export function decodeHeartbeat(data: Uint8Array): Heartbeat {
  const r = new Reader(data);
  if (r.u8() !== K_HBEAT) throw new Error('not hbeat');
  return { term: r.u32(), lastSeq: r.u32(), tick: r.u32(), sentAt: r.f64(), time: r.f32() };
}

export function encodePong(sentAt: number, lastSeq: number): Uint8Array {
  return new Writer(16).u8(K_PONG).f64(sentAt).u32(lastSeq).done();
}
export function decodePong(data: Uint8Array): { sentAt: number; lastSeq: number } {
  const r = new Reader(data);
  if (r.u8() !== K_PONG) throw new Error('not pong');
  return { sentAt: r.f64(), lastSeq: r.u32() };
}

// ------------------------------------------------- gameplay objects (drops)

/** A dropped item or an XP orb. Both are "things lying in the world a player
 *  picks up", so they share one message (kind tells the two apart). */
export const DK_ITEM = 0, DK_ORB = 1;

export interface DropState {
  id: number;
  kind: number;   // DK_ITEM | DK_ORB
  item: number;   // stack id (DK_ITEM) or points in the orb (DK_ORB)
  count: number;
  dmg: number;    // stack durability (DK_ITEM only)
  x: number; y: number; z: number;
  age: number;    // ticks (u16, wraps)
  /** A drop the host spawned for a client's request: the request id it came
   *  from, so the client knows which of its own drops this is (0 = none). */
  origin: number;
  /** Enchantments / name of the stack (DK_ITEM only), see stackExtras. */
  ex?: number[];
}

export interface DropsMsg { tick: number; keyframe: boolean; drops: DropState[]; removed: number[] }

export function encodeDrops(m: DropsMsg): Uint8Array {
  const w = new Writer(12 + m.drops.length * 34 + m.removed.length * 4)
    .u8(K_DROPS).u32(m.tick).u8(m.keyframe ? 1 : 0).u16(m.drops.length);
  for (const d of m.drops) {
    w.u8(d.kind).u32(d.id).u16(d.item & 0xffff).u8(Math.max(0, Math.min(255, d.count)))
      .u16(d.dmg & 0xffff).f32(d.x).f32(d.y).f32(d.z).u16(Math.max(0, Math.min(65535, d.age | 0)))
      .u16(Math.max(0, Math.min(65535, d.origin | 0)));
  }
  w.u16(m.removed.length);
  for (const id of m.removed) w.u32(id);
  // enchanted / named stacks trail the message: [drop id, extras]
  const fancy = m.drops.filter((d) => d.ex && d.ex.length);
  w.u16(fancy.length);
  for (const d of fancy) { w.u32(d.id); writeEx(w, d.ex); }
  return w.done();
}

export function decodeDrops(data: Uint8Array): DropsMsg {
  const r = new Reader(data);
  if (r.u8() !== K_DROPS) throw new Error('not drops');
  const tick = r.u32(), keyframe = r.u8() === 1, n = r.u16();
  const drops: DropState[] = [];
  for (let i = 0; i < n; i++) {
    const kind = r.u8(), id = r.u32(), item = r.u16(), count = r.u8(), dmg = r.u16();
    const x = r.f32(), y = r.f32(), z = r.f32(), age = r.u16(), origin = r.u16();
    if (kind !== DK_ITEM && kind !== DK_ORB) throw new Error('bad drop kind');
    if (![x, y, z].every(Number.isFinite) || item <= 0 || count <= 0) throw new Error('bad drop');
    drops.push({ id, kind, item, count, dmg, x, y, z, age, origin });
  }
  const rn = r.u16();
  const removed: number[] = [];
  for (let i = 0; i < rn; i++) removed.push(r.u32());
  if (r.left >= 2) {
    const byId = new Map(drops.map((d) => [d.id, d]));
    for (let i = r.u16(); i > 0; i--) {
      const d = byId.get(r.u32()), ex = readEx(r);
      if (d && ex) d.ex = ex;
    }
  }
  return { tick, keyframe, drops, removed };
}

export const AF_STUCK = 1, AF_PLAYER = 2, AF_CRIT = 4, AF_FLAME = 8;

export interface ArrowState { id: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; flags: number }

export interface ArrowsMsg { tick: number; keyframe: boolean; arrows: ArrowState[]; removed: number[] }

export function encodeArrows(m: ArrowsMsg): Uint8Array {
  const w = new Writer(12 + m.arrows.length * 30 + m.removed.length * 4)
    .u8(K_ARROWS).u32(m.tick).u8(m.keyframe ? 1 : 0).u16(m.arrows.length);
  for (const a of m.arrows) {
    w.u32(a.id).f32(a.x).f32(a.y).f32(a.z).f32(a.vx).f32(a.vy).f32(a.vz).u8(a.flags & 0xff);
  }
  w.u16(m.removed.length);
  for (const id of m.removed) w.u32(id);
  return w.done();
}

export function decodeArrows(data: Uint8Array): ArrowsMsg {
  const r = new Reader(data);
  if (r.u8() !== K_ARROWS) throw new Error('not arrows');
  const tick = r.u32(), keyframe = r.u8() === 1, n = r.u16();
  const arrows: ArrowState[] = [];
  for (let i = 0; i < n; i++) {
    const id = r.u32();
    const x = r.f32(), y = r.f32(), z = r.f32(), vx = r.f32(), vy = r.f32(), vz = r.f32(), flags = r.u8();
    if (![x, y, z, vx, vy, vz].every(Number.isFinite)) throw new Error('bad arrow');
    arrows.push({ id, x, y, z, vx, vy, vz, flags });
  }
  const rn = r.u16();
  const removed: number[] = [];
  for (let i = 0; i < rn; i++) removed.push(r.u32());
  return { tick, keyframe, arrows, removed };
}

// --------------------------------------------------- block entities (chests)

export const BE_CHEST = 0, BE_FURNACE = 1;

export interface BeEntry { kind: number; x: number; y: number; z: number; flat: number[] }
export interface BeMsg { keyframe: boolean; ents: BeEntry[]; removed: Array<{ kind: number; x: number; y: number; z: number }> }

export function encodeBe(m: BeMsg): Uint8Array {
  const w = new Writer(8 + m.ents.length * 24 + m.removed.length * 16).u8(K_BE).u8(m.keyframe ? 1 : 0).u16(m.ents.length);
  for (const e of m.ents) {
    w.u8(e.kind).i32(e.x).i32(e.y).i32(e.z).u16(e.flat.length);
    for (const v of e.flat) w.i32(Math.round(v));
  }
  w.u16(m.removed.length);
  for (const e of m.removed) w.u8(e.kind).i32(e.x).i32(e.y).i32(e.z);
  return w.done();
}

export function decodeBe(data: Uint8Array): BeMsg {
  const r = new Reader(data);
  if (r.u8() !== K_BE) throw new Error('not be');
  const keyframe = r.u8() === 1, n = r.u16();
  const ents: BeEntry[] = [];
  for (let i = 0; i < n; i++) {
    const kind = r.u8(), x = r.i32(), y = r.i32(), z = r.i32(), len = r.u16();
    if (kind !== BE_CHEST && kind !== BE_FURNACE || len > 1024 || len * 4 > r.left) throw new Error('bad be');
    const flat: number[] = [];
    for (let k = 0; k < len; k++) flat.push(r.i32());
    ents.push({ kind, x, y, z, flat });
  }
  const rn = r.u16();
  const removed: Array<{ kind: number; x: number; y: number; z: number }> = [];
  for (let i = 0; i < rn; i++) {
    const kind = r.u8();
    removed.push({ kind, x: r.i32(), y: r.i32(), z: r.i32() });
  }
  return { keyframe, ents, removed };
}

// ------------------------------------------------------- client -> host ops

/** A drop left the world: picked up by someone, burnt, or merged. `granted`
 *  means "and put it in your inventory" (the host hands a drop to a client). */
export interface TakeMsg { kind: number; id: number; item: number; count: number; dmg: number; granted: boolean; ex?: number[] }

export function encodeTake(t: TakeMsg): Uint8Array {
  const w = new Writer(16).u8(K_TAKE).u8(t.kind).u32(t.id).u16(t.item & 0xffff)
    .u8(Math.max(0, Math.min(255, t.count))).u16(t.dmg & 0xffff).u8(t.granted ? 1 : 0);
  writeEx(w, t.ex);
  return w.done();
}
export function decodeTake(data: Uint8Array): TakeMsg {
  const r = new Reader(data);
  if (r.u8() !== K_TAKE) throw new Error('not take');
  const kind = r.u8(), id = r.u32(), item = r.u16(), count = r.u8(), dmg = r.u16(), granted = r.u8() === 1;
  if (kind !== DK_ITEM && kind !== DK_ORB) throw new Error('bad take kind');
  return { kind, id, item, count, dmg, granted, ex: readEx(r) };
}

export interface DropReq { kind: number; req: number; item: number; count: number; dmg: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; ex?: number[] }

export function encodeDrop(d: DropReq): Uint8Array {
  const w = new Writer(40).u8(K_DROP).u8(d.kind).u32(d.req).u16(d.item & 0xffff).u8(Math.max(0, Math.min(255, d.count)))
    .u16(d.dmg & 0xffff).f32(d.x).f32(d.y).f32(d.z).f32(d.vx).f32(d.vy).f32(d.vz);
  writeEx(w, d.ex);
  return w.done();
}
export function decodeDrop(data: Uint8Array): DropReq {
  const r = new Reader(data);
  if (r.u8() !== K_DROP) throw new Error('not drop');
  const kind = r.u8(), req = r.u32(), item = r.u16(), count = r.u8(), dmg = r.u16();
  const x = r.f32(), y = r.f32(), z = r.f32(), vx = r.f32(), vy = r.f32(), vz = r.f32();
  if (kind !== DK_ITEM && kind !== DK_ORB || item <= 0 || count <= 0 || ![x, y, z].every(Number.isFinite)) throw new Error('bad drop req');
  return { kind, req, item, count, dmg, x, y, z, vx, vy, vz, ex: readEx(r) };
}

/** The client changed a block entity's contents (took from a chest, loaded a furnace). */
export function encodeBeReq(e: BeEntry): Uint8Array {
  const w = new Writer(24 + e.flat.length * 4).u8(K_BEREQ).u8(e.kind).i32(e.x).i32(e.y).i32(e.z).u16(e.flat.length);
  for (const v of e.flat) w.i32(Math.round(v));
  return w.done();
}
export function decodeBeReq(data: Uint8Array): BeEntry {
  const r = new Reader(data);
  if (r.u8() !== K_BEREQ) throw new Error('not bereq');
  const kind = r.u8(), x = r.i32(), y = r.i32(), z = r.i32(), len = r.u16();
  if (kind !== BE_CHEST && kind !== BE_FURNACE || len > 1024 || len * 4 > r.left) throw new Error('bad bereq');
  const flat: number[] = [];
  for (let i = 0; i < len; i++) flat.push(r.i32());
  return { kind, x, y, z, flat };
}

export function encodeTntReq(x: number, y: number, z: number): Uint8Array {
  return new Writer(16).u8(K_TNT).i32(x).i32(y).i32(z).done();
}
export function decodeTntReq(data: Uint8Array): { x: number; y: number; z: number } {
  const r = new Reader(data);
  if (r.u8() !== K_TNT) throw new Error('not tnt');
  return { x: r.i32(), y: r.i32(), z: r.i32() };
}

export const DAMAGE_KINDS = ['melee', 'projectile', 'explosion', 'fall', 'fire', 'lava', 'drown', 'starve', 'void', 'cactus', 'generic', 'sonic_boom'] as const;
export const DF_FIRE = 1;

/** The host tells a client that its player was hurt (mob melee, arrow, blast). */
export function encodeDamage(d: { amount: number; kind: string; x: number; y: number; z: number; attacker: number; kbx: number; kbz: number; fire: boolean }): Uint8Array {
  const ki = Math.max(0, DAMAGE_KINDS.indexOf(d.kind as (typeof DAMAGE_KINDS)[number]));
  return new Writer(28).u8(K_DMG).u8(Math.max(0, Math.min(255, Math.round(d.amount)))).u8(ki)
    .i32(Math.round(d.x)).i32(Math.round(d.y)).i32(Math.round(d.z)).i32(d.attacker | 0)
    .f32(d.kbx).f32(d.kbz).u8(d.fire ? DF_FIRE : 0).done();
}
export function decodeDamage(data: Uint8Array): { amount: number; kind: string; x: number; y: number; z: number; attacker: number; kbx: number; kbz: number; fire: boolean } {
  const r = new Reader(data);
  if (r.u8() !== K_DMG) throw new Error('not dmg');
  const amount = r.u8(), ki = r.u8();
  if (ki >= DAMAGE_KINDS.length) throw new Error('bad damage kind');
  const x = r.i32(), y = r.i32(), z = r.i32(), attacker = r.i32(), kbx = r.f32(), kbz = r.f32(), fire = r.u8() === DF_FIRE;
  if (!Number.isFinite(kbx) || !Number.isFinite(kbz)) throw new Error('bad knockback');
  return { amount, kind: DAMAGE_KINDS[ki], x, y, z, attacker, kbx, kbz, fire };
}

// ----------------------------------------------------------- edits packing

/** World delta (cx, cz, index, id) quads -> bytes and back. */
export function packQuads(q: Int32Array): Uint8Array {
  const w = new Writer(4 + q.length * 4).u32(q.length / 4);
  for (let i = 0; i < q.length; i++) w.i32(q[i]);
  return w.done();
}
export function unpackQuads(b: Uint8Array): Int32Array {
  const r = new Reader(b);
  const n = r.u32();
  if (n * 16 > r.left) throw new Error('bad quads');
  const out = new Int32Array(n * 4);
  for (let i = 0; i < out.length; i++) out[i] = r.i32();
  return out;
}

// ------------------------------------------------------------ compression

async function pipe(data: Uint8Array, stream: TransformStream<Uint8Array, Uint8Array>): Promise<Uint8Array> {
  const out = new Blob([data as BlobPart]).stream().pipeThrough(stream);
  return new Uint8Array(await new Response(out).arrayBuffer());
}

/** deflate-raw when CompressionStream exists (all evergreen browsers, node 18+); identity otherwise. */
export async function deflate(data: Uint8Array): Promise<Uint8Array> {
  if (typeof CompressionStream === 'undefined') return new Writer(data.length + 1).u8(0).bytes(data).done();
  const z = await pipe(data, new CompressionStream('deflate-raw') as unknown as TransformStream<Uint8Array, Uint8Array>);
  return new Writer(z.length + 1).u8(1).bytes(z).done();
}

export async function inflate(data: Uint8Array): Promise<Uint8Array> {
  if (data[0] === 0) return data.slice(1);
  if (typeof DecompressionStream === 'undefined') throw new Error('no DecompressionStream');
  return pipe(data.subarray(1), new DecompressionStream('deflate-raw') as unknown as TransformStream<Uint8Array, Uint8Array>);
}
